import { token } from '@/infrastructure/container';
import { redactKeys } from '@/domain/joinLink';
import type { IAppBus } from './AppBus';
import type { ILogger } from './Logger';

// `window.history` + `window.location`, wrapped. No routing library (INVARIANTS rule 12): this
// app has a handful of role screens and resolves which one from the URL a scanned share link
// landed on, which is a string match, not a router.
//
// `path` carries the search string AND the fragment, because that is where a share code rides:
// `#/join?project=…&pk=…&sk=…` (docs/access.md). Dropping either would make a scanned link
// indistinguishable from a cold open of the same route. Bearer keys live in the fragment on
// purpose — a fragment is never sent to a server and never lands in a gateway access log.
//
// `origin` is here so the admin store can build the join URLs the TV renders without a view
// reaching for `window` (INVARIANTS rule 12).

export interface IDomNavigation {
  start(): void;
  push(path: string): void;
  replace(path: string): void;
  get path(): string;
  get origin(): string;
}

export const TOKEN_IDomNavigation = token<IDomNavigation>('IDomNavigation');

export class DomNavigation implements IDomNavigation {
  private started = false;

  constructor(
    private readonly bus: IAppBus,
    private readonly logger: ILogger,
  ) {}

  // Idempotent; called once at boot after the subscribers are live, never from a constructor.
  // The initial publish is what drives role routing on a cold open of a scanned link, so it must
  // land on live handlers (INVARIANTS rule 20).
  start(): void {
    if (this.started) return;
    this.started = true;
    // popstate alone is enough for this app's fragment routing. Same-document fragment navigation
    // — setting location.hash, clicking a `#…` link, and back/forward across one — fires popstate
    // AND hashchange in both Chromium and jsdom, so a hashchange listener would only double every
    // publish. pushState/replaceState fire neither, which is why push() and replace() publish for
    // themselves below.
    window.addEventListener('popstate', this.onPopState);
    // Redacted, here and in the two below. This logs the whole address bar, and on a scanned link
    // the address bar is where both bearer keys are. `info` is the level a dev build ships with,
    // so unredacted this line puts live keys in a real browser console on every local run
    // (docs/access.md: a bearer key never reaches a log line). The BUS still gets the real path.
    this.logger.info('DomNavigation started at', redactKeys(this.path));
    this.bus.urlChanged(this.path);
  }

  push(path: string): void {
    this.logger.debug('nav.push', redactKeys(path));
    history.pushState(null, '', path);
    this.bus.urlChanged(path);
  }

  replace(path: string): void {
    this.logger.debug('nav.replace', redactKeys(path));
    history.replaceState(null, '', path);
    this.bus.urlChanged(path);
  }

  get path(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  get origin(): string {
    return window.location.origin;
  }

  private onPopState = (): void => {
    this.bus.urlChanged(this.path);
  };
}
